/**
 * Delte UI-primitiver — importér herfra i stedet for fra App.js
 * Alle komponenter bruger T (tema-singleton) fra ./tema
 *
 * Eksporterer: Glas, Label, Input, Textarea, Select, Knap,
 * Badge, Skeleton, EmptyState, TrendBadge
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { T } from '../tema';
import { useEscapeClose, post_crash_log, toast } from '../utils';
import { api } from '../api';
import DK from '../postnumre-dk.json';
// ── Glas ──────────────────────────────────────────────────
export function Glas({ children, style, padding = '20px 24px', radius = 16, hover = false, onClick }) {
 const [hov, setHov] = useState(false);
 return (
 <motion.div
 onClick={onClick}
 onHoverStart={() => hover && setHov(true)}
 onHoverEnd={() => hover && setHov(false)}
 whileHover={hover ? { y: -2, boxShadow: `0 12px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)` } : undefined}
 style={{
 background: hov ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.04)',
 border: `1px solid ${hov ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)'}`,
 borderRadius: radius,
 padding,
 backdropFilter: 'blur(20px)',
 WebkitBackdropFilter: 'blur(20px)',
 boxShadow: '0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)',
 transition: 'background 0.2s, border-color 0.2s',
 cursor: onClick ? 'pointer' : undefined,
 ...style,
 }}
 >
 {children}
 </motion.div>
 );
}
// ── Label ─────────────────────────────────────────────────
export function Label({ children, required, hjælp }) {
 return (
 <div style={{
 fontSize: 11, fontWeight: 700, color: T.dæmpet,
 letterSpacing: '0.1em', textTransform: 'uppercase',
 marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
 }}>
 {children}
 {required && <span style={{ color: T.roed, fontSize: 13 }}>*</span>}
 {hjælp && (
 <span title={hjælp} style={{ color: T.subtil, cursor: 'help', fontSize: 13 }}>ⓘ</span>
 )}
 </div>
 );
}
// ── Input ─────────────────────────────────────────────────
export function Input({ value, onChange, placeholder, type = 'text', disabled, autoFocus, style: customStyle, fejl, onBlur: externalBlur, ...rest }) {
 const [focused, setFocused] = useState(false);
 return (
 <> <input
 type={type}
 value={value}
 onChange={onChange}
 placeholder={placeholder}
 disabled={disabled}
 autoFocus={autoFocus}
 onFocus={() => setFocused(true)}
 onBlur={e => { setFocused(false); externalBlur && externalBlur(e); }}
 style={{
 width: '100%', boxSizing: 'border-box',
 background: 'rgba(255,255,255,0.04)',
 border: `1.5px solid ${fejl ? '#ff6b6b' : focused ? 'rgba(79,70,229,0.6)' : 'rgba(255,255,255,0.1)'}`,
 borderRadius: 10, padding: '10px 14px',
 color: T.creme, fontSize: 14,
 fontFamily: "'Inter',system-ui,sans-serif",
 outline: 'none',
 boxShadow: fejl ? '0 0 0 3px rgba(255,107,107,0.08)' : focused ? '0 0 0 3px rgba(79,70,229,0.1)' : 'none',
 transition: 'border-color 0.2s, box-shadow 0.2s',
 opacity: disabled ? 0.5 : 1,
 cursor: disabled ? 'not-allowed' : undefined,
 ...customStyle,
 }}
 {...rest}
 />
 {fejl && <div style={{ color: '#ff6b6b', fontSize: 12, marginTop: 4, marginBottom: 8, fontFamily: "'Inter',system-ui,sans-serif" }}>{fejl}</div>}
 </>
 );
}
// ── Textarea ──────────────────────────────────────────────
export function Textarea({ value, onChange, placeholder, rows = 4, disabled, style: customStyle, visCount, maxLength }) {
 const [focused, setFocused] = useState(false);
 const tæller = typeof value === 'string' ? value.length : 0;
 const nærGrænse = maxLength && tæller >= maxLength * 0.9;
 return (
 <div style={{ position: 'relative' }}> <textarea
 value={value}
 onChange={onChange}
 placeholder={placeholder}
 rows={rows}
 disabled={disabled}
 maxLength={maxLength}
 onFocus={() => setFocused(true)}
 onBlur={() => setFocused(false)}
 style={{
 width: '100%', boxSizing: 'border-box',
 background: 'rgba(255,255,255,0.04)',
 border: `1.5px solid ${focused ? 'rgba(79,70,229,0.6)' : 'rgba(255,255,255,0.1)'}`,
 borderRadius: 10, padding: '10px 14px',
 paddingBottom: visCount ? 28 : '10px',
 color: T.creme, fontSize: 14,
 fontFamily: "'Inter',system-ui,sans-serif",
 outline: 'none', resize: 'vertical',
 boxShadow: focused ? '0 0 0 3px rgba(79,70,229,0.1)' : 'none',
 transition: 'border-color 0.2s, box-shadow 0.2s',
 opacity: disabled ? 0.5 : 1,
 lineHeight: 1.6,
 ...customStyle,
 }}
 />
 {visCount && maxLength && (
 <div style={{
 position: 'absolute', bottom: 8, right: 12,
 fontSize: 11, fontFamily: "'Inter',system-ui,sans-serif",
 color: nærGrænse ? '#f59e0b' : 'rgba(255,255,255,0.35)',
 pointerEvents: 'none',
 }}>{tæller}/{maxLength}</div>
 )}
 </div>
 );
}
// ── Select ────────────────────────────────────────────────
export function Select({ value, onChange, children, options, disabled, style: customStyle, fejl }) {
 const [focused, setFocused] = useState(false);
 return (
 <> <select
 value={value}
 onChange={onChange}
 disabled={disabled}
 onFocus={() => setFocused(true)}
 onBlur={() => setFocused(false)}
 style={{
 width: '100%', boxSizing: 'border-box',
 background: 'rgba(15,19,35,0.95)',
 border: `1.5px solid ${fejl ? '#ff6b6b' : focused ? 'rgba(79,70,229,0.6)' : 'rgba(255,255,255,0.1)'}`,
 borderRadius: 10, padding: '10px 14px',
 color: T.creme, fontSize: 14,
 fontFamily: "'Inter',system-ui,sans-serif",
 outline: 'none', cursor: 'pointer',
 boxShadow: fejl ? '0 0 0 3px rgba(255,107,107,0.08)' : focused ? '0 0 0 3px rgba(79,70,229,0.1)' : 'none',
 transition: 'border-color 0.2s, box-shadow 0.2s',
 opacity: disabled ? 0.5 : 1,
 ...customStyle,
 }}
 >
 {options ? (
 <> <option value="">— Vælg —</option>
 {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
 </>
 ) : children}
 </select>
 {fejl && <div style={{ color: '#ff6b6b', fontSize: 12, marginTop: 4, marginBottom: 8, fontFamily: "'Inter',system-ui,sans-serif" }}>{fejl}</div>}
 </>
 );
}
// ── Knap ──────────────────────────────────────────────────
const KNAP_VARIANTER = {
 primær: {
 background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
 color: '#ffffff', border: 'none',
 boxShadow: '0 4px 14px rgba(79,70,229,0.3)',
 },
 sekundær: {
 background: 'rgba(255,255,255,0.06)',
 color: T.creme, border: '1px solid rgba(255,255,255,0.12)',
 },
 fare: {
 background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
 color: '#ffffff', border: 'none',
 boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
 },
 succes: {
 background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
 color: '#ffffff', border: 'none',
 boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
 },
 ghost: {
 background: 'transparent',
 color: T.dæmpet, border: '1px solid rgba(255,255,255,0.08)',
 },
};
export function Knap({
 children, onClick, variant = 'primær', disabled = false,
 loading = false, size = 'md', icon, style: customStyle, type = 'button',
}) {
 const v = KNAP_VARIANTER[variant] || KNAP_VARIANTER.primær;
 const størrelse = size === 'sm' ? { padding: '6px 12px', fontSize: 12, borderRadius: 8 }
 : size === 'lg' ? { padding: '14px 28px', fontSize: 15, borderRadius: 12 }
 : { padding: '10px 20px', fontSize: 13, borderRadius: 10 };
 // R4: sv-knap-ghost klasse tilføjes så lyst-tema CSS-override kan ramme ghost-varianten
 const klasse = variant === 'ghost' ? 'sv-knap-ghost' : undefined;
 return (
 <motion.button
 type={type}
 onClick={onClick}
 disabled={disabled || loading}
 className={klasse}
 whileHover={!disabled && !loading ? { scale: 1.02, y: -1 } : undefined}
 whileTap={!disabled && !loading ? { scale: 0.97 } : undefined}
 style={{
 ...v, ...størrelse,
 fontWeight: 600,
 fontFamily: "'Inter',system-ui,sans-serif",
 cursor: disabled || loading ? 'not-allowed' : 'pointer',
 opacity: disabled ? 0.5 : 1,
 display: 'inline-flex', alignItems: 'center', gap: 7,
 transition: 'background 0.2s, box-shadow 0.2s',
 position: 'relative', overflow: 'hidden',
 ...customStyle,
 }}
 >
 {loading && (
 <span style={{
 width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)',
 borderTopColor: '#ffffff', borderRadius: '50%',
 animation: 'spin 0.7s linear infinite', display: 'inline-block',
 }} />
 )}
 {!loading && icon && <span style={{ fontSize: size === 'sm' ? 13 : 15 }}>{icon}</span>}
 {children}
 </motion.button>
 );
}
// ── Badge ─────────────────────────────────────────────────
const BADGE_FARVER = {
 blå: { bg: 'rgba(96,165,250,0.15)', text: '#60a5fa', border: 'rgba(96,165,250,0.3)' },
 grøn: { bg: 'rgba(74,222,128,0.15)', text: '#4ade80', border: 'rgba(74,222,128,0.3)' },
 rød: { bg: 'rgba(248,113,113,0.15)', text: '#f87171', border: 'rgba(248,113,113,0.3)' },
 gul: { bg: 'rgba(251,191,36,0.15)', text: '#fbbf24', border: 'rgba(251,191,36,0.3)' },
 lilla: { bg: 'rgba(167,139,250,0.15)', text: '#a78bfa', border: 'rgba(167,139,250,0.3)' },
 turkis: { bg: 'rgba(52,211,153,0.15)', text: '#34d399', border: 'rgba(52,211,153,0.3)' },
 grå: { bg: 'rgba(148,163,184,0.1)', text: '#94a3b8', border: 'rgba(148,163,184,0.2)' },
};
export function Badge({ children, farve = 'grå', lille = false }) {
 const f = BADGE_FARVER[farve] || BADGE_FARVER.grå;
 return (
 <span style={{
 display: 'inline-flex', alignItems: 'center',
 background: f.bg, color: f.text,
 border: `1px solid ${f.border}`,
 borderRadius: 20, padding: lille ? '2px 8px' : '3px 10px',
 fontSize: lille ? 10 : 11, fontWeight: 600,
 letterSpacing: '0.05em',
 }}>
 {children}
 </span>
 );
}
// ── Skeleton ──────────────────────────────────────────────
export function Skeleton({ bredde = '100%', højde = 16, radius = 8, style: customStyle }) {
 return (
 <div className="sv-shimmer" style={{
 width: bredde, height: højde, borderRadius: radius, ...customStyle,
 }} />
 );
}
// ── EmptyState ────────────────────────────────────────────
export function EmptyState({ ikon = '', titel, beskrivelse, handling, handlingLabel = 'Kom i gang' }) {
 return (
 <motion.div
 initial={{ opacity: 0, y: 12 }}
 animate={{ opacity: 1, y: 0 }}
 style={{
 textAlign: 'center', padding: '48px 32px',
 background: 'rgba(255,255,255,0.02)',
 border: '1px dashed rgba(255,255,255,0.1)',
 borderRadius: 18,
 }}
 > <div style={{ fontSize: 40, marginBottom: 16, filter: 'grayscale(0.2)' }}>{ikon}</div>
 {titel && (
 <div style={{ fontSize: 16, fontWeight: 600, color: T.creme, marginBottom: 8 }}>{titel}</div>
 )}
 {beskrivelse && (
 <div style={{ fontSize: 13, color: T.dæmpet, lineHeight: 1.6, maxWidth: 320, margin: '0 auto' }}>
 {beskrivelse}
 </div>
 )}
 {handling && (
 <motion.button
 whileHover={{ scale: 1.03, y: -1 }}
 whileTap={{ scale: 0.97 }}
 onClick={handling}
 style={{
 marginTop: 20,
 background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
 border: 'none', borderRadius: 10, padding: '10px 24px',
 color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
 boxShadow: '0 4px 14px rgba(79,70,229,0.3)',
 fontFamily: "'Inter',system-ui,sans-serif",
 }}
 >
 {handlingLabel}
 </motion.button>
 )}
 </motion.div>
 );
}
// ── TrendBadge ────────────────────────────────────────────
export function TrendBadge({ værdi, enhed = '', retning = 'op', lille = false }) {
 const positiv = retning === 'op' ? værdi >= 0 : værdi <= 0;
 const farve = positiv ? '#4ade80' : '#f87171';
 const ikon = positiv ? '↑' : '↓';
 return (
 <span style={{
 display: 'inline-flex', alignItems: 'center', gap: 3,
 background: positiv ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
 border: `1px solid ${positiv ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)'}`,
 borderRadius: 8, padding: lille ? '2px 6px' : '3px 8px',
 fontSize: lille ? 10 : 12, fontWeight: 700, color: farve,
 }}> <span>{ikon}</span> <span>{Math.abs(værdi)}{enhed}</span> </span>
 );
}
// ══════════════════════════════════════════════════════════
// Udtrukket fra App.js (PR2 — 2026-05-23)
// ══════════════════════════════════════════════════════════
// V1.0 Telavox: Ring-op-knap. På desktop kaldes /api/telavox/ring-op som
// trigger click-to-dial via Telavox (ringer brugerens registrerede telefon).
// På mobil eller ved fallback: tel:-link så mobil-OS åbner Telavox-app/dialer.
export function RingOpKnap({ nummer, sag_id, klient_id, label, compact }) {
 if (!nummer) return null;
 const er_mobil = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
 const rensede = String(nummer).replace(/\s/g, '');
 const tel_link = 'tel:' + rensede;
 const stil_compact = { fontSize: 12, color: '#06d6a0', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 4 };
 const stil_full = { display:'inline-flex', alignItems:'center', gap:6, padding:'6px 12px', background:'rgba(6,214,160,0.12)', border:'1px solid rgba(6,214,160,0.35)', borderRadius:8, color:'#06d6a0', fontSize:13, fontWeight:600, cursor:'pointer', textDecoration:'none', fontFamily:"'Inter',system-ui,sans-serif" };
 const stil = compact ? stil_compact : stil_full;
 const klik = async (e) => {
 if (er_mobil) return; // tel:-link håndterer mobil
 e.preventDefault();
 toast('Starter opkald via Telavox…', 'info');
 try {
 const r = await api('telavox/ring-op', 'POST', { nummer: rensede, sag_id, klient_id });
 if (r && r.succes) {
 toast('Telavox ringer dig op nu', 'success');
 } else if (r && r.fallback_tel) {
 // Fallback til tel:-link
 window.location.href = tel_link;
 } else {
 window.location.href = tel_link;
 }
 } catch (_) {
 window.location.href = tel_link;
 }
 };
 return (
 <a href={tel_link} onClick={klik} aria-label={`Ring ${nummer}`} title={`Ring op til ${nummer}`} style={stil}>
 {label || nummer}
 </a>
 );
}
// Bekræftelsesdialog — erstatning for window.confirm()
export function BekræftDialog({ besked, onJa, onNej, farve }) {
 useEscapeClose(true, onNej);
 const annuller_ref = useRef(null);
 // Fix #20: flyt fokus til dialog ved åbning (tilgængelighed + fat-finger sikring)
 useEffect(() => { if (annuller_ref.current) annuller_ref.current.focus(); }, []);
 return createPortal(
 <div role="alertdialog" aria-modal="true" aria-label="Bekræft handling"
 style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:100001, display:"flex", alignItems:"center", justifyContent:"center" }}
 onClick={onNej}> <div style={{ background:"rgba(10,14,46,0.98)", border:T.kort_kant, borderRadius:16, padding:"28px 32px", maxWidth:400, width:"90%" }}
 onClick={e => e.stopPropagation()}> <div style={{ fontSize:15, color:"#e2e8f0", lineHeight:1.6, marginBottom:20 }}>{besked}</div> <div style={{ display:"flex", gap:10 }}> <button ref={annuller_ref} onClick={onNej}
 style={{ flex:1, padding:"12px", borderRadius:8, border:T.skillelinje, background:"transparent", color:"#b0bec5", fontSize:14, cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif" }}>
 Annuller
 </button> <button onClick={onJa}
 style={{ flex:1, padding:"12px", borderRadius:8, border:"none", background:farve||"#e74c3c", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif" }}>
 Ja, fortsæt
 </button> </div> </div> </div>,
 document.body
 );
}
// ═════════════════════════════════════════════════════════════════════
// MOBIL BOTTOM-NAV — erstatter sidebar på mobil portrait
// ═════════════════════════════════════════════════════════════════════
export function MobilBottomNav({ visning, naviger, log_ud, bruger }) {
 // 4 items — Admin FJERNET fra bottom (sikkerhed) og ligger på long-press af profil
 const SVG_HJEM = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
 const SVG_SAGER = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>;
 const SVG_SOEG = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
 const SVG_DASH = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>;
 const SVG_OS = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>;
 const items = [
 { id: "menu", ikon: SVG_HJEM, label: "Hjem" },
 { id: "klienter", ikon: SVG_SAGER, label: "Sager" },
 { id: "søg", ikon: SVG_SOEG, label: "Søg" },
 { id: bruger?.os_admin ? "os" : "dashboard", ikon: bruger?.os_admin ? SVG_OS : SVG_DASH, label: bruger?.os_admin ? "OS" : "Dashboard" },
 ];
 const kan_admin = ['direktoer','systemadmin','sagsbehandler','specialist'].includes(bruger?.rolle);
 const profil_trykket_tid = React.useRef(0);
 const long_press_timer = React.useRef(null);
 // Skjul sagsportalens bundnav når OS-dashboardet er aktivt —
 // OS har sin egen .sv-os-bottomnav og de to må ikke overlappe.
 // VIGTIGT: early return SKAL stå efter alle hook-kald (Rules of Hooks).
 if (visning === 'os') return null;
 const profil_start = () => {
 profil_trykket_tid.current = Date.now();
 long_press_timer.current = setTimeout(() => {
 if (kan_admin) {
 // Haptic feedback hvis understøttet
 if (navigator.vibrate) navigator.vibrate(50);
 naviger("indstillinger");
 }
 }, 600);
 };
 const profil_slut = () => {
 if (long_press_timer.current) clearTimeout(long_press_timer.current);
 const varighed = Date.now() - profil_trykket_tid.current;
 if (varighed < 600) naviger("min-profil"); // kort tryk → profil
 };
 const profil_aktiv = visning === "min-profil" || visning === "indstillinger";
 return (
 <div className="sv-bottom-nav" style={{
 position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
 background: "var(--sv-bottom-nav-bg, #080c1a)", borderTop: "1px solid var(--sv-bottom-nav-border, rgba(255,255,255,0.06))",
 boxShadow: "var(--sv-bottom-nav-shadow, 0 -4px 12px rgba(0,0,0,0.3))",
 height: 72, paddingBottom: "env(safe-area-inset-bottom, 0)",
 }}>
 {items.map(item => {
 const aktiv = visning === item.id;
 return (
 <div key={item.id} onClick={() => naviger(item.id)}
 style={{
 flex: 1, display: "flex", flexDirection: "column",
 alignItems: "center", justifyContent: "center",
 padding: "8px 4px", cursor: "pointer",
 color: aktiv ? "#3fd4c7" : "#94a3b8",
 borderTop: aktiv ? "2px solid #3fd4c7" : "2px solid transparent",
 minHeight: 48,
 }}> <span style={{ lineHeight: 1, marginBottom: 2, display:"flex", alignItems:"center", justifyContent:"center" }}>{item.ikon}</span> <span style={{ fontSize: 12, fontWeight: aktiv ? 600 : 400 }}>{item.label}</span> </div>
 );
 })}
 {/* Profil med long-press → Admin */}
 <div onMouseDown={profil_start} onMouseUp={profil_slut} onMouseLeave={() => clearTimeout(long_press_timer.current)}
 onTouchStart={profil_start} onTouchEnd={profil_slut} onTouchCancel={() => clearTimeout(long_press_timer.current)}
 title={kan_admin ? "Tryk kort: Profil · Hold nede: Admin" : "Profil"}
 style={{
 flex: 1, display: "flex", flexDirection: "column",
 alignItems: "center", justifyContent: "center",
 padding: "8px 4px", cursor: "pointer",
 color: profil_aktiv ? "#3fd4c7" : "#94a3b8",
 borderTop: profil_aktiv ? "2px solid #3fd4c7" : "2px solid transparent",
 minHeight: 48, userSelect: "none", WebkitTouchCallout: "none",
 }}> <span style={{ fontSize: 22, lineHeight: 1, marginBottom: 2, fontWeight: 700 }}>
 {bruger?.initialer || ""}
 </span>
 {/* Fix #2: vis hint til admin-brugere at long-press giver adgang til indstillinger */}
 <span style={{ fontSize: 12, fontWeight: profil_aktiv ? 600 : 400 }}>Profil</span>
 {/* #13: "hold:"-tooltip-fragment skjult — vises som rå tekst i DOM; bruger title-attribut i stedet */}
 </div> </div>
 );
}
// ═════════════════════════════════════════════════════════════════════
// GLOBAL ERROR BOUNDARY — Forhindrer hvid skærm ved React-crash
// Fanger render-fejl, poster crash til server, viser recovery.
//
// Auto-reset (kompakt): ved et forbigående server-blip (502/netværk
// under deploy) forsøger kompakte boundaries automatisk at re-montere
// børn efter AUTO_RESET_MS. Det eliminerer kravet om manuelt klik ved
// kortvarige fejl. Globale (ikke-kompakte) boundaries auto-resetter
// ikke — de kræver altid brugerhandling.
// ═════════════════════════════════════════════════════════════════════
const AUTO_RESET_MS = 5000; // ms — kompakt boundary auto-reset
export class ErrorBoundary extends React.Component {
 constructor(props) {
   super(props);
   this.state = { fejl: null, autoResetSek: null };
   this._autoResetTimer = null;
   this._nedtællingInterval = null;
 }
 static getDerivedStateFromError(fejl) { return { fejl, autoResetSek: null }; }
 componentDidCatch(fejl, info) {
   console.error(`[ErrorBoundary ${this.props.navn || "global"}]`, fejl, info?.componentStack);
   post_crash_log(fejl, info, this.props.navn || "global");
   // Kompakte boundaries (sub-paneler) starter auto-reset — globale gør ikke.
   if (this.props.kompakt) {
     this._startAutoReset();
   }
 }
 _startAutoReset() {
   this._rydTimere();
   const sekunder = Math.ceil(AUTO_RESET_MS / 1000);
   this.setState({ autoResetSek: sekunder });
   this._nedtællingInterval = setInterval(() => {
     this.setState(s => {
       if (s.autoResetSek == null || s.autoResetSek <= 1) return s;
       return { autoResetSek: s.autoResetSek - 1 };
     });
   }, 1000);
   this._autoResetTimer = setTimeout(() => {
     this._rydTimere();
     this.setState({ fejl: null, autoResetSek: null });
   }, AUTO_RESET_MS);
 }
 _rydTimere() {
   clearTimeout(this._autoResetTimer);
   clearInterval(this._nedtællingInterval);
 }
 componentWillUnmount() { this._rydTimere(); }
 nulstil = () => { this._rydTimere(); this.setState({ fejl: null, autoResetSek: null }); };
 render() {
 if (this.state.fejl) {
 const kompakt = this.props.kompakt;
 const sek = this.state.autoResetSek;
 return (
 <div style={{
 minHeight: kompakt ? 200 : "100vh",
 background: kompakt ? "rgba(231,76,60,0.06)" : "#0a0e2e",
 color: "#e2e8f0",
 padding: kompakt ? "24px" : "clamp(24px,5vw,60px)",
 fontFamily: "'Inter',system-ui,sans-serif",
 display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
 borderRadius: kompakt ? 14 : 0,
 border: kompakt ? "1px solid rgba(231,76,60,0.2)" : "none",
 }}> <div style={{ fontSize: kompakt ? 36 : 56, marginBottom: kompakt ? 12 : 20 }}></div> <div style={{ fontSize: kompakt ? 16 : 22, fontWeight: 300, color: "#fff", marginBottom: 10, textAlign: "center" }}>
 {kompakt ? "Kunne ikke vise denne sektion" : "Noget gik galt med visningen"}
 </div> <div style={{ fontSize: kompakt ? 12 : 14, color: "#94a3b8", marginBottom: 20, textAlign: "center", maxWidth: 540 }}>
 {kompakt
 ? (sek != null
     ? `Forsøger automatisk igen om ${sek} sek…`
     : `Fejlen er logget. Prøv at genåbne${this.props.navn ? " '" + this.props.navn + "'" : " siden"} eller gå til en anden fane.`)
 : "Siden kunne ikke vises. Prøv igen, eller gå tilbage til forsiden."}
 </div> <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}> <button onClick={this.nulstil} style={{
 background: "#3fd4c7", border: "none", borderRadius: 10, padding: "12px 22px",
 color: "#0c0f1a", fontWeight: 700, cursor: "pointer", fontSize: 14, minHeight: 44,
 }}> Prøv igen nu</button>
 {!kompakt && (
 <button onClick={() => { window.location.href = "/"; }} style={{
 background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
 borderRadius: 10, padding: "12px 22px", color: "#cbd5e1", cursor: "pointer", fontSize: 14, minHeight: 44,
 }}> Til forsiden</button>
 )}
 </div>
 {process.env.NODE_ENV !== "production" && this.state.fejl?.message && (
 <pre style={{
 marginTop: 20, fontSize: 11, color: "#64748b", maxWidth: 700,
 whiteSpace: "pre-wrap", overflow: "auto", maxHeight: 150,
 background: "rgba(0,0,0,0.3)", padding: 12, borderRadius: 8,
 }}>{String(this.state.fejl.message || this.state.fejl)}</pre>
 )}
 </div>
 );
 }
 return this.props.children;
 }
}
// Kompakt wrapper — bruges til admin-faner og sub-visninger
export function SubBoundary({ navn, children }) {
 return <ErrorBoundary navn={navn} kompakt={true}>{children}</ErrorBoundary>;
}
// Safe wrapper — render fallback hvis noget i children throws
export function Safe({ children, fallback = null, navn }) {
 try { return <>{children}</>; }
 catch (e) {
 console.warn(`[Safe ${navn || ""}]`, e);
 post_crash_log(e, null, "Safe:" + (navn || "?"));
 return fallback;
 }
}
// HjælpIkon — lille ? med tooltip for kontekstuel hjælp
export function HjaelpIkon({ tekst, placering="top" }) {
 const [vis, setVis] = useState(false);
 return (
 <span style={{ position:"relative", display:"inline-block", marginLeft:6, verticalAlign:"middle" }}> <span
 onMouseEnter={()=>setVis(true)}
 onMouseLeave={()=>setVis(false)}
 onClick={()=>setVis(v=>!v)}
 tabIndex={0}
 onFocus={()=>setVis(true)}
 onBlur={()=>setVis(false)}
 role="button"
 aria-label={`Hjælp: ${tekst}`}
 style={{
 display:"inline-flex", alignItems:"center", justifyContent:"center",
 width:16, height:16, borderRadius:"50%",
 background:"rgba(63,212,199,0.15)", border:"1px solid rgba(63,212,199,0.35)",
 color:"#3fd4c7", fontSize:11, fontWeight:700, cursor:"help", userSelect:"none",
 fontFamily:"'Inter',system-ui,sans-serif", lineHeight:1,
 }}
 >?</span>
 {vis && (
 <span style={{
 position:"absolute",
 [placering==="bottom" ? "top" : "bottom"]: "calc(100% + 8px)",
 left:"50%", transform:"translateX(-50%)",
 background:"rgba(15,23,42,0.98)", color:"#e2e8f0",
 border:"1px solid rgba(63,212,199,0.3)", borderRadius:8,
 padding:"10px 14px", fontSize:12, lineHeight:1.55,
 width:280, maxWidth:"80vw", zIndex:10000,
 boxShadow:"0 8px 24px rgba(0,0,0,0.5)",
 whiteSpace:"normal", textAlign:"left", fontWeight:400,
 pointerEvents:"none",
 }}>{tekst}</span>
 )}
 </span>
 );
}
// Minimal markdown-renderer — håndterer headings, bold, italic, kode,
// lister, tabeller, links, hr. Sikker: escaper HTML, bygger React-elementer.
export function renderMarkdown(md) {
 if (!md) return null;
 const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
 const inline = (tekst) => {
 // Bold **x**
 let r = escape(tekst);
 r = r.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
 // Italic _x_ eller *x*
 r = r.replace(/(^|\s)_(.+?)_(?=\s|$)/g, '$1<em>$2</em>');
 // Inline code `x`
 r = r.replace(/`([^`]+)`/g, '<code style="background:rgba(63,212,199,0.1);padding:2px 6px;border-radius:4px;font-size:0.92em;color:#3fd4c7">$1</code>');
 // Links [text](url) — only allow http/https/relative to prevent javascript: XSS
 r = r.replace(/\[(.+?)\]\((\S+?)\)/g, (_, txt, url) => {
 const safe = /^(https?:\/\/|\/)/i.test(url) ? url : '#';
 return `<a href="${safe}" target="_blank" rel="noopener" style="color:#3fd4c7;text-decoration:underline">${txt}</a>`;
 });
 return r;
 };
 const linjer = md.split(/\r?\n/);
 const ud = [];
 let i = 0;
 let n = 0;
 while (i < linjer.length) {
 const l = linjer[i];
 if (!l.trim()) { i++; continue; }
 // Headings
 const h = l.match(/^(#{1,6})\s+(.+)$/);
 if (h) {
 const niv = h[1].length;
 const Tag = `h${Math.min(niv, 6)}`;
 const stilPrNiveau = {
 1: { fontSize:22, fontWeight:600, color:"#e2e8f0", marginTop:18, marginBottom:12, borderBottom:"1px solid rgba(63,212,199,0.2)", paddingBottom:8 },
 2: { fontSize:18, fontWeight:600, color:T.turkis, marginTop:22, marginBottom:10 },
 3: { fontSize:15, fontWeight:600, color:"#e2e8f0", marginTop:14, marginBottom:6 },
 4: { fontSize:14, fontWeight:600, color:"#cbd5e1", marginTop:10, marginBottom:4 },
 };
 ud.push(React.createElement(Tag, { key:n++, style: stilPrNiveau[niv] || stilPrNiveau[4], dangerouslySetInnerHTML:{ __html: inline(h[2]) } }));
 i++; continue;
 }
 // Horizontal rule
 if (/^-{3,}$/.test(l.trim())) {
 ud.push(<hr key={n++} style={{ border:"none", borderTop:"1px solid rgba(255,255,255,0.1)", margin:"16px 0" }} />);
 i++; continue;
 }
 // Code block ```
 if (l.trim().startsWith('```')) {
 const kode = [];
 i++;
 while (i < linjer.length && !linjer[i].trim().startsWith('```')) { kode.push(linjer[i]); i++; }
 i++; // skip afsluttende ```
 ud.push(
 <pre key={n++} style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(63,212,199,0.15)", borderRadius:8, padding:"12px 16px", overflowX:"auto", fontSize:12, color:"#cbd5e1", fontFamily:"'Courier New',monospace", margin:"10px 0" }}>
 {kode.join('\n')}
 </pre>
 );
 continue;
 }
 // Table (| header | header |)
 if (l.trim().startsWith('|') && linjer[i+1] && /^\|[\s:|-]+\|$/.test(linjer[i+1].trim())) {
 const header = l.trim().split('|').slice(1, -1).map(c => c.trim());
 i += 2;
 const rows = [];
 while (i < linjer.length && linjer[i].trim().startsWith('|')) {
 rows.push(linjer[i].trim().split('|').slice(1, -1).map(c => c.trim()));
 i++;
 }
 ud.push(
 <div key={n++} style={{ overflowX:"auto", margin:"10px 0" }}> <table style={{ borderCollapse:"collapse", width:"100%", fontSize:13, color:"#cbd5e1" }}> <thead> <tr>{header.map((h, j) => <th key={j} style={{ background:"rgba(63,212,199,0.08)", border:"1px solid rgba(63,212,199,0.2)", padding:"8px 12px", textAlign:"left", fontWeight:600, color:T.turkis }} dangerouslySetInnerHTML={{ __html: inline(h) }} />)}</tr> </thead> <tbody>
 {rows.map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k} style={{ border:"1px solid rgba(255,255,255,0.08)", padding:"8px 12px" }} dangerouslySetInnerHTML={{ __html: inline(c) }} />)}</tr>)}
 </tbody> </table> </div>
 );
 continue;
 }
 // Unordered list
 if (/^[-*]\s+/.test(l)) {
 const items = [];
 while (i < linjer.length && /^[-*]\s+/.test(linjer[i])) {
 items.push(linjer[i].replace(/^[-*]\s+/, ''));
 i++;
 }
 ud.push(
 <ul key={n++} style={{ paddingLeft:22, margin:"8px 0", color:"#cbd5e1", fontSize:13, lineHeight:1.7 }}>
 {items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}
 </ul>
 );
 continue;
 }
 // Ordered list
 if (/^\d+\.\s+/.test(l)) {
 const items = [];
 while (i < linjer.length && /^\d+\.\s+/.test(linjer[i])) {
 items.push(linjer[i].replace(/^\d+\.\s+/, ''));
 i++;
 }
 ud.push(
 <ol key={n++} style={{ paddingLeft:22, margin:"8px 0", color:"#cbd5e1", fontSize:13, lineHeight:1.7 }}>
 {items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}
 </ol>
 );
 continue;
 }
 // Paragraph
 const para = [l];
 i++;
 while (i < linjer.length && linjer[i].trim() && !/^(#|[-*]\s|\d+\.|\||```|-{3,})/.test(linjer[i])) {
 para.push(linjer[i]);
 i++;
 }
 ud.push(
 <p key={n++} style={{ fontSize:13, color:"#cbd5e1", lineHeight:1.7, margin:"8px 0" }}
 dangerouslySetInnerHTML={{ __html: inline(para.join(' ')) }} />
 );
 }
 return ud;
}
// HjælpManualVisning — vises under visning==="hjaelp"
export function HjaelpManualVisning() {
 const [manualer, setManualer] = useState([]);
 const [valgt, setValgt] = useState(null);
 const [indhold, setIndhold] = useState(null);
 const [søg, setSøg] = useState("");
 const [søg_res, setSøgRes] = useState([]);
 const [indlæser, setIndlæser] = useState(true);
 React.useEffect(() => {
 api("hjaelp/manualer").then(d => {
 if (d?.manualer) {
 setManualer(d.manualer);
 if (d.manualer.length > 0) {
 // Start med rolle-relevant manual
 setValgt(d.manualer[0].id);
 }
 }
 setIndlæser(false);
 }).catch(() => setIndlæser(false));
 }, []);
 React.useEffect(() => {
 if (!valgt) return;
 setIndhold(null);
 api(`hjaelp/manualer/${valgt}`).then(d => { if (d?.indhold) setIndhold(d); }).catch(() => setIndhold(null));
 }, [valgt]);
 // Debounced søg
 React.useEffect(() => {
 const q = søg.trim();
 if (q.length < 2) { setSøgRes([]); return; }
 const t = setTimeout(() => {
 api(`hjaelp/soeg?q=${encodeURIComponent(q)}`).then(d => {
 if (d?.resultater) setSøgRes(d.resultater);
 }).catch(() => setSøgRes([]));
 }, 300);
 return () => clearTimeout(t);
 }, [søg]);
 return (
 <div className="sv-hjaelp-layout" style={{ display:"flex", gap:16, padding:"clamp(16px, 3vw, 28px) clamp(16px, 4vw, 40px)", maxWidth:1200, margin:"0 auto", position:"relative", zIndex:1 }}>
 {/* Sidebar med manualer */}
 <div style={{ width:260, flexShrink:0 }}> <input
 type="text"
 value={søg}
 onChange={e => setSøg(e.target.value)}
 placeholder=" Søg i manualer..."
 style={{ width:"100%", background:"rgba(255,255,255,0.05)", border:T.kort_kant, borderRadius:8, padding:"10px 14px", color:T.creme, fontSize:13, marginBottom:12, boxSizing:"border-box" }}
 />
 {søg_res.length > 0 && (
 <div style={{ background:"rgba(63,212,199,0.05)", border:`1px solid ${T.turkis}33`, borderRadius:10, padding:10, marginBottom:12, maxHeight:300, overflowY:"auto" }}> <div style={{ fontSize:11, color:T.turkis, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:8 }}>{søg_res.length} resultater</div>
 {søg_res.map((r, i) => (
 <div key={i}
 onClick={() => { setValgt(r.manual_id); setSøg(""); setSøgRes([]); }}
 style={{ padding:"8px 10px", background:"rgba(255,255,255,0.03)", borderRadius:6, marginBottom:4, cursor:"pointer", fontSize:12 }}> <div style={{ color:T.turkis, fontSize:11, marginBottom:2 }}>{r.manual_titel} · linje {r.linje}</div> <div style={{ color:"#cbd5e1", fontSize:12, whiteSpace:"pre-wrap", fontFamily:"'Courier New',monospace" }}>{r.kontekst.slice(0, 140)}{r.kontekst.length>140?"...":""}</div> </div>
 ))}
 </div>
 )}
 <div style={{ fontSize:11, color:"#94a3b8", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:8, padding:"0 4px" }}>Manualer</div>
 {indlæser ? (
 <div style={{ color:"#94a3b8", fontSize:12, padding:10 }}>Henter...</div>
 ) : manualer.length === 0 ? (
 <div style={{ color:"#94a3b8", fontSize:12, padding:10 }}>Ingen manualer tilgængelige</div>
 ) : manualer.map(m => (
 <div key={m.id}
 onClick={() => setValgt(m.id)}
 style={{
 padding:"10px 12px", borderRadius:8, cursor:"pointer", marginBottom:4,
 background: valgt === m.id ? "rgba(63,212,199,0.1)" : "transparent",
 borderLeft: valgt === m.id ? `3px solid ${T.turkis}` : "3px solid transparent",
 transition:"all 0.15s",
 }}> <div style={{ fontSize:13, fontWeight: valgt===m.id?600:500, color: valgt===m.id?T.turkis:"#cbd5e1" }}>{m.titel}</div> <div style={{ fontSize:11, color:"#94a3b8", marginTop:2, lineHeight:1.4 }}>{m.beskrivelse}</div> </div>
 ))}
 </div>
 {/* Manual-indhold */}
 <div style={{ flex:1, minWidth:0, position:"relative", overflow:"hidden", background:"rgba(255,255,255,0.02)", border:T.kort_kant, borderRadius:14, padding:"24px clamp(20px, 3vw, 32px)" }}> <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${T.turkis}, ${T.turkis}44, transparent)`, borderRadius:"14px 14px 0 0", pointerEvents:"none" }} />
 {indhold ? (
 <> <div style={{ fontSize:11, color:"#94a3b8", marginBottom:4 }}>Senest opdateret: {new Date(indhold.opdateret).toLocaleDateString("da-DK")}</div>
 {renderMarkdown(indhold.indhold)}
 </>
 ) : valgt ? (
 <div style={{ color:"#94a3b8", padding:40, textAlign:"center" }}>Henter manual...</div>
 ) : (
 <div style={{ color:"#94a3b8", padding:40, textAlign:"center" }}>Vælg en manual fra venstre menu</div>
 )}
 </div> </div>
 );
}
export function PostnrBy({ postnr, by, setPostnr, setBy }) {
 const [søg, setSøg] = useState(by || "");
 const [forslag, setForslag] = useState([]);
 const [vis, setVis] = useState(false);
 useEffect(() => { setSøg(by || ""); }, [by]);
 const håndterNr = e => {
 const v = e.target.value.replace(/\D/g,"").slice(0,4);
 setPostnr(v);
 if (v.length===4 && DK[v]) { setBy(DK[v]); setSøg(DK[v]); setVis(false); }
 else if (v.length===4) { setBy(""); setSøg(""); }
 };
 const håndterBy = e => {
 const v = e.target.value; setSøg(v); setBy(v);
 if (v.length>=2) {
 const m = Object.entries(DK).filter(([,b])=>b.toLowerCase().startsWith(v.toLowerCase())).slice(0,7).map(([nr,b])=>({nr,b}));
 setForslag(m); setVis(true);
 } else { setForslag([]); setVis(false); }
 };
 const vælg = item => { setBy(item.b); setPostnr(item.nr); setSøg(item.b); setVis(false); setForslag([]); };
 return (
 <div style={{ display:"grid", gridTemplateColumns:"minmax(100px, 1fr) minmax(150px, 2fr)", gap:"0 12px" }}> <div> <Label req>Postnr.</Label> <Input value={postnr} onChange={håndterNr} placeholder="XXXX"
 style={{ borderColor: postnr.length===4&&DK[postnr] ? T.turkis : postnr.length===4 ? T.roed : "rgba(255,255,255,0.06)" }} /> </div> <div style={{ position:"relative" }}> <Label req>By</Label> <input value={søg} onChange={håndterBy}
 onBlur={()=>setTimeout(()=>setVis(false),150)}
 onFocus={()=>søg.length>=2&&setVis(true)}
 placeholder="Søg eller skriv by..."
 className="sv-input"
 style={{ width:"100%", background:"rgba(8,12,30,0.65)", border:`1px solid ${by?T.turkis+"66":"rgba(255,255,255,0.09)"}`, borderRadius:9, padding:"12px 15px", color:T.creme, fontSize:14, marginBottom:16 }} />
 {vis && forslag.length>0 && (
 <div style={{ position:"absolute", top:46, left:0, right:0, zIndex:400, background:"#0d1240", border:`1px solid ${T.turkis}44`, borderRadius:10, overflow:"hidden", boxShadow:"0 16px 48px rgba(0,0,0,0.6)" }}>
 {forslag.map((item,i)=>(
 <div key={i} onMouseDown={()=>vælg(item)}
 style={{ padding:"11px 15px", cursor:"pointer", fontSize:13, color:T.creme, display:"flex", justifyContent:"space-between", borderBottom:i<forslag.length-1?T.skillelinje:"none" }}
 onMouseOver={e=>e.currentTarget.style.background="rgba(63,212,199,0.1)"}
 onMouseOut={e=>e.currentTarget.style.background="transparent"}> <span>{item.b}</span> <span style={{color:T.dæmpet,fontFamily:"DM Mono, monospace",fontSize:12}}>{item.nr}</span> </div>
 ))}
 </div>
 )}
 </div> </div>
 );
}
// ─────────────────────────────────────────────────────────
// CPR FELT — Fix #13: maskeret visning ved redigering
// ─────────────────────────────────────────────────────────
export function CprFelt({ value, onChange, fejl }) {
 const fmt = v => { const t=v.replace(/\D/g,"").slice(0,10); return t.length<=6?t:t.slice(0,6)+"-"+t.slice(6); };
 const gyldig = value.length===11;
 return (
 <div style={{ marginBottom: 16 }}> <input type="text" inputMode="numeric" autoComplete="off" value={value} onChange={e=>onChange(fmt(e.target.value))}
 placeholder="fx 010190-1234" maxLength={11}
 className="sv-input"
 style={{ width:"100%", background:"rgba(8,12,30,0.65)", border:`1px solid ${fejl?T.roed:gyldig?T.turkis+"66":"rgba(255,255,255,0.09)"}`, borderRadius:9, padding:"12px 15px", color:T.creme, fontSize:14, letterSpacing:value.length>6?"0.06em":"normal", fontFamily:"DM Mono, monospace" }} /> <div style={{ display:"flex", justifyContent:"space-between", marginTop:5, paddingLeft:2 }}>
 {fejl ? <span style={{color:T.roed,fontSize:12}}>{fejl}</span> : gyldig ? <span style={{color:T.turkis,fontSize:12}}> Gyldigt format</span> : <span style={{color:T.dæmpet,fontSize:12}}>{value.replace("-","").length}/10 cifre</span>}
 <span style={{fontSize:12,color:T.subtil}}> Krypteres</span> </div> </div>
 );
}
// ─────────────────────────────────────────────────────────
// FIL VEDHÆFTNING
// ─────────────────────────────────────────────────────────
export function Filer({ filer, onChange }) {
 const ref = useRef(null);
 const fmt = b => b<1048576?(b/1024).toFixed(0)+" KB":(b/1048576).toFixed(1)+" MB";
 const ikon = t => t?.includes("pdf")?"":t?.includes("image")?"":"";
 const tilfoej = e => { onChange([...filer,...Array.from(e.target.files).map(f=>({id:Date.now()+Math.random(),navn:f.name,størrelse:f.size,type:f.type}))]); e.target.value=""; };
 return (
 <div> <input ref={ref} type="file" multiple onChange={tilfoej} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx" style={{display:"none"}} />
 {filer.length===0 ? (
 <div onClick={()=>ref.current.click()} style={{ border:"1px dashed rgba(255,255,255,0.06)", borderRadius:9, padding:"18px 16px", textAlign:"center", cursor:"pointer", transition:"all 0.2s" }}
 onMouseOver={e=>{e.currentTarget.style.borderColor=T.turkis+"66";e.currentTarget.style.background="rgba(63,212,199,0.04)"}}
 onMouseOut={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.06)";e.currentTarget.style.background="transparent"}}> <div style={{fontSize:24,marginBottom:6}}></div> <div style={{color:T.lys,fontSize:13}}>Klik for at vedhæfte filer</div> <div style={{color:T.dæmpet,fontSize:12,marginTop:3}}>PDF · Word · Billeder · Excel</div> </div>
 ) : (
 <div>
 {filer.map(f=>(
 <div key={f.id} style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(0,0,0,0.2)", border:T.skillelinje, borderRadius:7, padding:"9px 12px", marginBottom:6 }}> <span style={{fontSize:18}}>{ikon(f.type)}</span> <div style={{flex:1,minWidth:0}}> <div style={{color:T.creme,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.navn}</div> <div style={{color:T.dæmpet,fontSize:12}}>{fmt(f.størrelse)}</div> </div> <button onClick={()=>onChange(filer.filter(x=>x.id!==f.id))} style={{background:"none",border:"none",color:T.roed,fontSize:18,padding:"0 4px"}}>×</button> </div>
 ))}
 <button onClick={()=>ref.current.click()} style={{ width:"100%", background:"none", border:"1px dashed rgba(255,255,255,0.08)", borderRadius:7, padding:"9px", color:T.dæmpet, fontSize:12, marginTop:4 }}>+ Tilføj flere</button> </div>
 )}
 </div>
 );
}
// ─────────────────────────────────────────────────────────
// NY KLIENT — TRIN-NAVIGATION (fri hopping)
// ─────────────────────────────────────────────────────────
export const TRIN_META = [
 { id:"klient", label:"Klient", ikon:"", farve:"#3fd4c7" },
 { id:"sag", label:"Sag", ikon:"", farve:"#60a5fa" },
 { id:"modpart", label:"Modpart", ikon:"", farve:"#ffd166" },
 { id:"boern", label:"Børn", ikon:"", farve:"#c084fc" },
 { id:"noter", label:"Noter", ikon:"", farve:"#06d6a0" },
 { id:"bekræft", label:"OK", ikon:"", farve:"#3fd4c7" },
];
export const SAGSTYPER = [
 {v:"leads",l:"Leads"},
 {v:"skilsmisse",l:"Skilsmisse"},{v:"samvaer",l:"Samvær"},
 {v:"bopael",l:"Bopæl"},{v:"forældremyndighed",l:"Forældremyndighed"},
 {v:"boern_bidrag",l:"Børnebidrag"},{v:"myndighedsdialog",l:"Myndighedsdialog"},
 {v:"andet",l:"Andet"},
];
// Normaliser sagstype-værdi til sammenligning (håndterer æøå, casing, mellemrum)
// Gamle sager har type som enum ("skilsmisse") — nye sager har type som navn ("Skilsmisse")
// Begge skal mappe til samme sag_typer-række.
export function normaliser_sagstype(s) {
 return String(s || '').toLowerCase()
 .replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa')
 .replace(/[^a-z0-9]/g, '');
}
// H5 fix: Slår kodeværdi op til dansk label — case-insensitive + capitalize fallback
export function sagstype_label(v) {
 if (!v || v === "—") return v || "—";
 const lower = v.toLowerCase();
 const match = SAGSTYPER.find(x => x.v === lower || x.l.toLowerCase() === lower);
 if (match) return match.l;
 // Fallback: capitalize first letter for ukendte sagstyper
 return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}
export const FORÆLDREMY = [
 {v:"faelles",l:"Fælles"},{v:"klient_alene",l:"Klient alene"},
 {v:"modpart_alene",l:"Modpart alene"},{v:"uafklaret",l:"Uafklaret"},
];
export const BOPÆL_OPT = [
 {v:"klient",l:"Hos klient"},{v:"modpart",l:"Hos modpart"},
 {v:"delt",l:"Delt bopæl"},{v:"andet",l:"Andet/uafklaret"},
];
// Fix #18: TrinNavigation — reducér padding og fontSize på meget small screens
export function TrinNavigation({ aktivt, setAktivt, status }) {
 const lille = typeof window !== "undefined" && window.innerWidth < 480;
 const fyldbare = ["klient","sag","modpart","boern","noter"];
 const udfyldt = fyldbare.filter(k => status[k]).length;
 const procent = Math.round((udfyldt / fyldbare.length) * 100);
 return (
 <div>
 {/* Progress bar */}
 {/* #10: Progress-tekst — kontrast forbedret (0.4 → 0.7) */}
 <div style={{ padding: lille ? "8px 12px 4px" : "10px 24px 4px", display:"flex", alignItems:"center", gap:10 }}> <div style={{ flex:1, height:4, borderRadius:2, background:"rgba(255,255,255,0.06)", overflow:"hidden" }}> <div style={{ height:"100%", width:`${procent}%`, borderRadius:2, background:"linear-gradient(90deg,#3fd4c7,#60a5fa)", transition:"width 0.4s ease" }} /> </div> <span style={{ fontSize:12, color:"rgba(255,255,255,0.75)", fontFamily:"'Inter',system-ui,sans-serif", whiteSpace:"nowrap", minWidth:60, textAlign:"right", fontWeight:500 }}>{udfyldt} af {fyldbare.length} trin · {procent}%</span> </div> <div style={{ display:"flex", gap:0, overflowX:"auto", padding: lille ? "0 6px" : "0 20px", position:"relative" }}>
 {/* Progress line */}
 <div style={{ position:"absolute", bottom:0, left:20, right:20, height:2, background:"rgba(255,255,255,0.05)", zIndex:0 }} />
 {TRIN_META.map((t,i) => {
 const er_aktiv = aktivt === t.id;
 const har_data = status[t.id];
 return (
 <button key={t.id} onClick={()=>setAktivt(t.id)}
 className={er_aktiv ? "sv-wizard-step-aktiv" : undefined}
 style={{
 flex:1, minWidth: lille ? 48 : 60, padding: lille ? "10px 4px 8px" : "14px 8px 12px", background:"none", border:"none",
 borderBottom: er_aktiv ? `2px solid ${t.farve}` : har_data ? `2px solid ${t.farve}44` : "2px solid rgba(255,255,255,0.06)",
 color: er_aktiv ? t.farve : har_data ? T.lys : T.dæmpet,
 cursor:"pointer", transition:"all 0.25s cubic-bezier(0.34,1.56,0.64,1)", display:"flex", flexDirection:"column",
 alignItems:"center", gap:6, position:"relative", zIndex:1,
 boxShadow: er_aktiv ? `0 4px 0 ${t.farve}88` : "none",
 }}> <div style={{ width:34, height:34, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15,
 background: er_aktiv ? `linear-gradient(135deg, ${t.farve}30, ${t.farve}15)` : har_data ? `${t.farve}15` : "transparent",
 border: `1px solid ${er_aktiv ? t.farve : har_data ? t.farve+"55" : "rgba(255,255,255,0.06)"}`,
 boxShadow: er_aktiv ? `0 0 16px ${t.farve}40, inset 0 1px 0 rgba(255,255,255,0.1)` : har_data ? `0 0 8px ${t.farve}20` : "none",
 transition:"all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
 transform: er_aktiv ? "scale(1.1)" : "scale(1)",
 }}>
 {har_data && !er_aktiv ? <span style={{color:t.farve,fontSize:12,fontWeight:700}}></span> : t.ikon}
 </div> <span className="sv-wizard-step-label" style={{ fontSize: lille ? 12 : 12, letterSpacing: lille ? "0.05em" : "0.1em", textTransform:"uppercase", fontWeight: er_aktiv ? 700 : 400 }}>{t.label}</span> </button>
 );
 })}
 </div> </div>
 );
}